/**
 * TextMessageData.js
 * Talkroute
 *
 * Created by Vel Genov on 3/13/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import moment, { Moment } from "moment";
import validateArray from "../../helpers/arrayValidator";
import { getFormattedDateAndTime } from "../../helpers/dateFormatter";
import {
  textMessageAttachmentJSONParser,
  textMessageAttachmentRehydrator,
} from "./TextMessageAttachment";

/**
 * A constant that represents the message direction - incoming or outgoing
 */
export enum MessageDirection {
  INCOMING = "incoming",
  OUTGOING = "outgoing",
}

/**
 * A data class representing a text message
 *
 * @param body a string containing the body of the message
 * @param read a boolean that shows if the message is read
 * @param isLast a boolean the shows if this is the last message in a conversation
 * @param userEmail the email of the user that sent the text message
 * @param talkrouteNumber the Talkroute number used for this conversation
 * @param contactNumber the phone number of the other participat in the conversation
 * @param direction incoming or otgoing message
 * @param id the message unique id
 * @param createdAtMicroseconds an exact number of when the message was created. Used for comparison
 * @param createdAt a user friendly date object of when the message was created
 * @param updatedAt a date object that shows when the message was last updated
 * @param attachments an array of TextMessageAttachment, if message attachments are available
 */
export class TextMessageData {
  public id: number;
  public body: string;
  public isRead: boolean;
  public isLast: boolean;
  public userEmail: string;
  public talkrouteNumber: number;
  public contactNumber: number;
  public messageDirection: MessageDirection;
  public createdAtMicroseconds: number;
  public createdAt: Moment;
  public updatedAt: Moment;
  public attachments: any[];

  constructor(opts) {
    this.id = opts.id;
    this.body = opts.body;
    this.isRead = opts.read;
    this.isLast = opts.isLast;
    this.userEmail = opts.userEmail;
    this.talkrouteNumber = opts.talkrouteNumber;
    this.contactNumber = opts.contactNumber;
    this.messageDirection =
      opts.direction.toString().toLowerCase() === MessageDirection.INCOMING
        ? MessageDirection.INCOMING
        : MessageDirection.OUTGOING;
    this.createdAtMicroseconds = opts.createdAtMicroseconds;
    this.createdAt = moment(opts.createdAt);
    this.updatedAt = moment(opts.updatedAt);
    this.attachments = opts.attachments;
  }

  /**
   * Check if this is an incoming message. Otherwise it's outgoing
   *
   * @returns true if the message is incoming. False otherwise
   */
  isIncoming = () => this.messageDirection === MessageDirection.INCOMING;

  /**
   * Get the createdAt date formatted
   *
   * @returns a formatted string for createdAt
   */
  getFormattedDateAndTime = () => getFormattedDateAndTime(this.createdAt);
}

/**
 * Parses the incoming JSON text message data. Creates a TextMessageData object for each line item
 *
 * @param {*} textMessageData an array containig text message data.
 */
export const textMessageJSONParser = (textMessageData) => {
  // If valid data is passed, proceed. Otherwise don't return anything
  if (validateArray(textMessageData)) {
    // Process each entry
    const parsedData = textMessageData.map((entry) => {
      const {
        body,
        read,
        created_at_micro_seconds: createdAtMicroseconds,
        is_last_message: isLast,
        user_email: userEmail,
        talkroute_number: talkrouteNumber,
        contact_number: contactNumber,
        message_direction: direction,
        id,
        created_at: createdAt,
        updated_at: updatedAt,
        attachments: attachmentsUnparsed,
      } = entry;

      const attachments = textMessageAttachmentJSONParser(attachmentsUnparsed);

      const textMessageEntry = new TextMessageData({
        body,
        read,
        createdAtMicroseconds,
        isLast,
        userEmail,
        talkrouteNumber,
        contactNumber,
        direction,
        id,
        createdAt,
        updatedAt,
        attachments,
      });

      return textMessageEntry;
    });

    return parsedData;
  }
};

/**
 * Parses the incoming generic objects into TextMessageData objects.
 * Checks for empty objects passed in
 *
 * @param {*} textMessageData an array containig text messages.
 */
export const textMessagesRehydrator = (textMessagesData) => {
  // If valid data is passed, proceed. Otherwise don't return anything
  if (validateArray(textMessagesData)) {
    // Process each entry
    const parsedData = textMessagesData.map((entry) =>
      rehydrateTextMessage(entry)
    );

    return parsedData;
  }
  return [];
};

/**
 * Parses the incoming single generic object into TextMessageData.
 * Checks for an empty object being passed in.
 *
 * @param {*} textMessage a single generic object containing a text message.
 */
export const rehydrateTextMessage = (textMessage) => {
  if (textMessage) {
    const {
      body,
      isRead,
      isLast,
      userEmail,
      talkrouteNumber,
      contactNumber,
      messageDirection,
      id,
      createdAtMicroseconds,
      createdAt,
      updatedAt,
      attachmentsUnparsed,
    } = textMessage;

    const attachments = textMessageAttachmentRehydrator(attachmentsUnparsed);

    const textMessageEntry = new TextMessageData({
      body,
      read: isRead,
      isLast,
      userEmail,
      talkrouteNumber,
      contactNumber,
      direction: messageDirection,
      id,
      createdAtMicroseconds,
      createdAt,
      updatedAt,
      attachments,
    });

    return textMessageEntry;
  }
  return textMessage;
};
